import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { timeEntriesAPI, projectsAPI, usersAPI } from '../../services/api';
import {
  ActivityIcon,
  ClockIcon,
  CheckCircleIcon,
  UserPlusIcon,
  FolderIcon,
  SearchAltIcon,
} from '../icons';

interface Activity {
  id: number;
  type: 'time_entry' | 'project' | 'user' | 'approval';
  title: string;
  description: string;
  timestamp: Date;
  icon: 'clock' | 'check' | 'user' | 'folder';
  color: 'blue' | 'emerald' | 'amber' | 'violet';
  link?: string;
  resourceId?: number;
}

const Icons = {
  activity: <ActivityIcon className="w-7 h-7" strokeWidth={1.5} />,
  clock: <ClockIcon className="w-5 h-5" strokeWidth={2} />,
  check: <CheckCircleIcon className="w-5 h-5" strokeWidth={2} />,
  user: <UserPlusIcon className="w-5 h-5" strokeWidth={2} />,
  folder: <FolderIcon className="w-5 h-5" strokeWidth={2} />,
  search: <SearchAltIcon className="w-5 h-5" strokeWidth={2} />,
};

const RecentActivityPage: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<
    'all' | 'time_entry' | 'project' | 'user' | 'approval'
  >('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchActivities = async () => {
      setLoading(true);
      try {
        // Fetch recent data from various sources
        const [timeEntriesRes, projectsRes, usersRes] = await Promise.all([
          timeEntriesAPI.getAll({ limit: 10 }),
          projectsAPI.getAll(),
          usersAPI.getAll(),
        ]);

        const recentActivities: Activity[] = [];

        // Add time entries as activities
        const timeEntries = timeEntriesRes.data.time_entries || [];
        timeEntries.slice(0, 5).forEach((entry: any, index: number) => {
          recentActivities.push({
            id: entry.id || index,
            type: 'time_entry',
            title:
              entry.status === 'pending'
                ? t('activity.timeEntryPending')
                : t('activity.timeEntrySubmitted'),
            description: `${entry.user?.full_name || t('common.unknown')} - ${entry.project?.name || t('common.noProject')}`,
            timestamp: new Date(entry.created_at || Date.now()),
            icon: 'clock',
            color: entry.status === 'pending' ? 'blue' : 'emerald',
            link:
              entry.status === 'pending'
                ? '/manager/approvals'
                : '/manager/time-entries',
            resourceId: entry.id,
          });
        });

        // Add recent projects
        const projects = projectsRes.data.projects || [];
        projects.slice(0, 3).forEach((project: any) => {
          recentActivities.push({
            id: project.id + 1000,
            type: 'project',
            title: t('activity.projectUpdated'),
            description: project.name,
            timestamp: new Date(project.updated_at || Date.now()),
            icon: 'folder',
            color: 'violet',
            link: `/manager/projects/${project.id}`,
            resourceId: project.id,
          });
        });

        // Add system activities (milestone, invitations) - these match the dashboard
        recentActivities.push({
          id: 9001,
          type: 'approval',
          title: t('activity.projectMilestone'),
          description: t('manager.sprintDeliverablesApproved'),
          timestamp: new Date(Date.now() - 86400000), // 1 day ago
          icon: 'check',
          color: 'emerald',
          link: '/manager/projects',
        });

        recentActivities.push({
          id: 9002,
          type: 'user',
          title: t('activity.userInvitation'),
          description: t('manager.seniorDeveloperAwaiting'),
          timestamp: new Date(Date.now() - 172800000), // 2 days ago
          icon: 'user',
          color: 'amber',
          link: '/manager/users',
        });

        // Sort by timestamp
        recentActivities.sort(
          (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
        );

        setActivities(recentActivities);
      } catch (error) {
        console.error('Failed to fetch activities:', error);
        // Set some mock data on error
        setActivities([
          {
            id: 1,
            type: 'time_entry',
            title: t('activity.timeEntryPending'),
            description: '25 entries waiting for review',
            timestamp: new Date(),
            icon: 'clock',
            color: 'blue',
            link: '/manager/approvals',
          },
          {
            id: 2,
            type: 'approval',
            title: t('activity.projectMilestone'),
            description: 'Sprint deliverables approved',
            timestamp: new Date(Date.now() - 86400000),
            icon: 'check',
            color: 'emerald',
            link: '/manager/projects',
          },
          {
            id: 3,
            type: 'user',
            title: t('activity.userInvitation'),
            description: 'Senior Developer role - awaiting response',
            timestamp: new Date(Date.now() - 172800000),
            icon: 'user',
            color: 'amber',
            link: '/manager/users',
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [t]);

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('activity.justNow');
    if (diffMins < 60) return t('activity.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('activity.hoursAgo', { count: diffHours });
    if (diffDays === 1) return t('activity.yesterday');
    if (diffDays < 7) return t('activity.daysAgo', { count: diffDays });
    return date.toLocaleDateString();
  };

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; icon: string; text: string }> = {
      blue: {
        bg: isDark ? 'bg-blue-500/10' : 'bg-blue-50',
        icon: isDark
          ? 'bg-blue-500/20 text-blue-400'
          : 'bg-blue-100 text-blue-600',
        text: isDark ? 'text-blue-400' : 'text-blue-600',
      },
      emerald: {
        bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50',
        icon: isDark
          ? 'bg-emerald-500/20 text-emerald-400'
          : 'bg-emerald-100 text-emerald-600',
        text: isDark ? 'text-emerald-400' : 'text-emerald-600',
      },
      amber: {
        bg: isDark ? 'bg-amber-500/10' : 'bg-amber-50',
        icon: isDark
          ? 'bg-amber-500/20 text-amber-400'
          : 'bg-amber-100 text-amber-600',
        text: isDark ? 'text-amber-400' : 'text-amber-600',
      },
      violet: {
        bg: isDark ? 'bg-violet-500/10' : 'bg-violet-50',
        icon: isDark
          ? 'bg-violet-500/20 text-violet-400'
          : 'bg-violet-100 text-violet-600',
        text: isDark ? 'text-violet-400' : 'text-violet-600',
      },
    };
    return colors[color] || colors.blue;
  };

  const filteredActivities = activities.filter((activity) => {
    const matchesFilter = filter === 'all' || activity.type === filter;
    const matchesSearch =
      !searchQuery ||
      activity.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 rounded-2xl'
    : 'bg-white border border-slate-200 rounded-2xl shadow-sm';

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Sticky Header */}
      <div
        className={`sticky top-0 z-20 ${isDark ? 'bg-slate-900' : 'bg-slate-50'} pb-4 -mx-6 px-6 pt-0`}
      >
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white shadow-lg shadow-cyan-500/25">
              {Icons.activity}
            </div>
            <div>
              <h1
                className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {t('activity.title')}
              </h1>
              <p
                className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {filteredActivities.length} {t('activity.activities')}
              </p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div
          className={`p-4 rounded-2xl ${isDark ? 'bg-slate-900/50 border border-slate-700/50' : 'bg-white border border-slate-200 shadow-sm'}`}
        >
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <div
                className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {Icons.search}
              </div>
              <input
                type="text"
                placeholder={t('activity.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-10 pr-4 py-2.5 rounded-xl ${
                  isDark
                    ? 'bg-slate-700/50 border border-slate-600 text-white placeholder-slate-400 focus:ring-2 focus:ring-cyan-500 focus:border-transparent'
                    : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-cyan-500 focus:border-transparent'
                }`}
              />
            </div>
            {/* Type Filter */}
            <div className="w-full sm:w-48">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className={`w-full px-4 py-2.5 rounded-xl ${
                  isDark
                    ? 'bg-slate-700/50 border border-slate-600 text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent'
                    : 'bg-slate-50 border border-slate-200 text-slate-900 focus:ring-2 focus:ring-cyan-500 focus:border-transparent'
                }`}
              >
                <option value="all">{t('activity.allTypes')}</option>
                <option value="time_entry">{t('activity.timeEntries')}</option>
                <option value="project">{t('activity.projects')}</option>
                <option value="user">{t('activity.users')}</option>
                <option value="approval">{t('activity.approvals')}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Activity List */}
      <div className={cardClass}>
        {filteredActivities.length === 0 ? (
          <div
            className={`p-12 text-center ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
          >
            <div
              className={`w-16 h-16 mx-auto mb-4 rounded-full ${isDark ? 'bg-slate-900' : 'bg-slate-100'} flex items-center justify-center`}
            >
              {Icons.search}
            </div>
            <p
              className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('activity.noResults')}
            </p>
            <p className="text-sm mt-1">{t('activity.tryDifferentFilters')}</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-700/50">
            {filteredActivities.map((activity) => {
              const colors = getColorClasses(activity.color);
              const content = (
                <>
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors.icon}`}
                  >
                    {Icons[activity.icon]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}
                    >
                      {activity.title}
                    </p>
                    <p
                      className={`text-sm truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                    >
                      {activity.description}
                    </p>
                  </div>
                  <span className={`text-xs whitespace-nowrap ${colors.text}`}>
                    {formatTimeAgo(activity.timestamp)}
                  </span>
                </>
              );

              const baseClasses = `flex items-center gap-4 p-4 ${colors.bg} first:rounded-t-2xl last:rounded-b-2xl`;
              const hoverClasses = isDark
                ? 'hover:bg-opacity-75'
                : 'hover:bg-opacity-75';

              return activity.link ? (
                <Link
                  key={activity.id}
                  to={activity.link}
                  className={`${baseClasses} ${hoverClasses} transition-all hover:scale-[1.01] cursor-pointer`}
                >
                  {content}
                </Link>
              ) : (
                <div key={activity.id} className={baseClasses}>
                  {content}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentActivityPage;
